import { useUser } from 'contexts/UserContext'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import {
  LuBusFront,
  LuMoveRight,
  LuTrafficCone,
  LuUser,
  LuX
} from 'react-icons/lu'
import { Link } from 'wouter'

export default function DashboardComp({ fetchedIncidents, fetchedLogs }) {
  const { user } = useUser()

  const currentYear = new Date().getFullYear()

  const formatDate = (dateString) => {
    const date = new Date(dateString)

    const day = String(date.getUTCDate()).padStart(2, '0')
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const year = date.getUTCFullYear()
    const hours = String(date.getUTCHours()).padStart(2, '0')
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')

    return `${day}/${month}/${year} ${hours}:${minutes} h`
  }

  // Usuario actual
  const userFirstName = user?.first_name || ''
  const userLastName =
    user?.last_name && user?.last_name.trim() !== ''
      ? ' ' + user?.last_name
      : ''

  // Log circulación
  const circulationLastImportLog = useMemo(() => {
    return fetchedLogs?.data?.find((log) =>
      log.details.toLowerCase().includes('circulación')
    )
  }, [fetchedLogs?.data])

  // Última importación de circulación
  const circulationLastImportDate = circulationLastImportLog?.created_at
    ? formatDate(circulationLastImportLog.created_at)
    : ''

  // Último importador de circulación
  const circulationFirstName = circulationLastImportLog?.user_first_name || ''
  const circulationLastName =
    circulationLastImportLog?.user_last_name &&
    circulationLastImportLog?.user_last_name.trim() !== ''
      ? ' ' + circulationLastImportLog.user_last_name
      : ''

  // Log lesionados
  const injuredLastImportLog = useMemo(() => {
    return fetchedLogs?.data?.find((log) =>
      log.details.toLowerCase().includes('lesionados')
    )
  }, [fetchedLogs?.data])

  // Última importación de lesionados
  const injuredLastImportDate = injuredLastImportLog?.created_at
    ? formatDate(injuredLastImportLog.created_at)
    : ''

  // Último importador de lesionados
  const injuredFirstName = injuredLastImportLog?.user_first_name || ''
  const injuredLastName =
    injuredLastImportLog?.user_last_name &&
    injuredLastImportLog?.user_last_name.trim() !== ''
      ? ' ' + injuredLastImportLog.user_last_name
      : ''

  return (
    <div className="h-fit dark:bg-gray-900">
      <div className="w-full md:px-6 md:py-10 h-fit">
        <h1 className="text-2xl font-semibold text-gray-800 capitalize lg:text-3xl dark:text-white">
          Bienvenido{' '}
          <span className="underline decoration-accent">
            {userFirstName + userLastName}
          </span>
          !
        </h1>

        <div className="grid w-full grid-cols-12 mt-8 gap-y-8 md:gap-8 xl:mt-12 justify-items-center">
          {/* Siniestros TUS - (Año actual) */}
          <div className="w-full max-w-md col-span-12 px-8 py-4 mt-16 bg-white rounded-lg shadow-lg md:col-span-6 xl:col-span-4 dark:bg-gray-800">
            <div className="flex items-center justify-center -mt-16 md:justify-end">
              <LuTrafficCone
                className={`object-cover p-2 w-20 h-20 float-left rounded-full block border-2 bg-[#FCAE52] border-[#FCAE52] dark:border-[#FCAE52]`}
              />
            </div>

            <h2 className="mt-2 text-xl font-semibold text-gray-800 dark:text-white md:mt-0">
              Siniestros TUS
            </h2>

            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md ring-1 ring-inset ring-gray-600/50">
              {currentYear}
            </span>

            {/* Cantidad */}
            <div className="flex flex-wrap items-center mt-2 mb-4 text-sm text-gray-600 dark:text-gray-200">
              Se han registrado
              <span className="font-semibold underline decoration-[#FCAE52] px-1">
                {fetchedIncidents?.error ? (
                  <LuX className="text-red-600" size={20} />
                ) : fetchedIncidents?.loading ? (
                  <ImSpinner9 size={16} className="animate-spin" />
                ) : (
                  fetchedIncidents?.data?.filter((incident) =>
                    incident?.occurrence_date?.includes(currentYear)
                  )?.length
                )}
              </span>
              siniestros TUS.
            </div>
          </div>

          {/* Siniestros TUS - Circulación - (Año actual) */}
          <div className="w-full max-w-md col-span-12 px-8 py-4 mt-16 bg-white rounded-lg shadow-lg md:col-span-6 xl:col-span-4 dark:bg-gray-800">
            <div className="flex justify-center -mt-16 md:justify-end">
              <LuBusFront
                className={`object-cover p-2 w-20 h-20 float-left rounded-full block border-2 bg-[#FCAE52] border-[#FCAE52] dark:border-[#FCAE52]`}
              />
            </div>

            <h2 className="mt-2 text-xl font-semibold text-gray-800 dark:text-white md:mt-0">
              Siniestros TUS - Circulación
            </h2>

            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md ring-1 ring-inset ring-gray-600/50">
              {currentYear}
            </span>

            <div className="flex flex-col">
              {/* Cantidad */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Se han registrado
                <span className="font-semibold underline decoration-[#FCAE52] px-1">
                  {fetchedIncidents?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedIncidents?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : (
                    fetchedIncidents?.data?.filter(
                      (incident) =>
                        incident?.type === 'circulation' &&
                        incident?.occurrence_date?.includes(currentYear)
                    )?.length
                  )}
                </span>
                siniestros TUS de circulación.
              </div>

              {/* Última importación */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Última importación:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="mx-1 animate-spin" />
                  ) : circulationLastImportLog ? (
                    <>{circulationLastImportDate}.</>
                  ) : null}
                </span>
              </div>

              {/* Último importador */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Último importador:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : circulationLastImportLog ? (
                    <>{circulationFirstName + circulationLastName}.</>
                  ) : null}
                </span>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Link
                title="Ver: Siniestros TUS - Circulación"
                href={`/tus-incidents/circulation/grid`}
                className="flex items-center justify-center space-x-2 font-medium text-[#FCAE52] dark:text-[#FCAE52]"
                tabIndex="0"
                role="link"
              >
                <span>Ir</span>
                <LuMoveRight size={20} />
              </Link>
            </div>
          </div>

          {/* Siniestros TUS - Lesionados - (Año actual) */}
          <div className="w-full max-w-md col-span-12 px-8 py-4 mt-16 bg-white rounded-lg shadow-lg md:col-span-6 xl:col-span-4 dark:bg-gray-800">
            <div className="flex justify-center -mt-16 md:justify-end">
              <LuUser
                className={`object-cover p-2 w-20 h-20 float-left rounded-full block border-2 bg-[#FCAE52] border-[#FCAE52] dark:border-[#FCAE52]`}
              />
            </div>

            <h2 className="mt-2 text-xl font-semibold text-gray-800 dark:text-white md:mt-0">
              Siniestros TUS - Lesionados
            </h2>

            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md ring-1 ring-inset ring-gray-600/50">
              {currentYear}
            </span>

            <div className="flex flex-col">
              {/* Cantidad */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Se han registrado
                <span className="font-semibold underline decoration-[#FCAE52] px-1">
                  {fetchedIncidents?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedIncidents?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : (
                    fetchedIncidents?.data?.filter(
                      (incident) =>
                        incident?.type === 'injured' &&
                        incident?.occurrence_date?.includes(currentYear)
                    )?.length
                  )}
                </span>
                siniestros TUS de lesionados.
              </div>

              {/* Última importación */}
              <div className="flex flex-wrap items-center mt-2 space-x-1 text-sm text-gray-600 dark:text-gray-200">
                Última importación:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : injuredLastImportLog ? (
                    <>{injuredLastImportDate}.</>
                  ) : null}
                </span>
              </div>

              {/* Último importador */}
              <div className="flex flex-wrap items-center mt-2 space-x-1 text-sm text-gray-600 dark:text-gray-200">
                Último importador:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : injuredLastImportLog ? (
                    <>{injuredFirstName + injuredLastName}.</>
                  ) : null}
                </span>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Link
                title="Ver: Siniestros TUS - Lesionados"
                href={`/tus-incidents/injured/grid`}
                className="flex items-center justify-center space-x-2 font-medium text-[#FCAE52] dark:text-[#FCAE52]"
                tabIndex="0"
                role="link"
              >
                <span>Ir</span>
                <LuMoveRight size={20} />
              </Link>
            </div>
          </div>

          {/* Siniestros TUS - (General) */}
          <div className="w-full max-w-md col-span-12 px-8 py-4 mt-16 bg-white rounded-lg shadow-lg md:col-span-6 xl:col-span-4 dark:bg-gray-800">
            <div className="flex items-center justify-center -mt-16 md:justify-end">
              <LuTrafficCone
                className={`object-cover p-2 w-20 h-20 float-left rounded-full block border-2 bg-[#FCAE52] border-[#FCAE52] dark:border-[#FCAE52]`}
              />
            </div>

            <h2 className="mt-2 text-xl font-semibold text-gray-800 dark:text-white md:mt-0">
              Siniestros TUS
            </h2>

            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md ring-1 ring-inset ring-gray-600/50">
              2025 – {currentYear}
            </span>

            {/* Cantidad */}
            <div className="flex flex-wrap items-center mt-2 mb-4 text-sm text-gray-600 dark:text-gray-200">
              Se han registrado
              <span className="font-semibold underline decoration-[#FCAE52] px-1">
                {fetchedIncidents?.error ? (
                  <LuX className="text-red-600" size={20} />
                ) : fetchedIncidents?.loading ? (
                  <ImSpinner9 size={16} className="animate-spin" />
                ) : (
                  fetchedIncidents?.data?.length
                )}
              </span>
              siniestros TUS.
            </div>
          </div>

          {/* Siniestros TUS - Circulación - (General) */}
          <div className="w-full max-w-md col-span-12 px-8 py-4 mt-16 bg-white rounded-lg shadow-lg md:col-span-6 xl:col-span-4 dark:bg-gray-800">
            <div className="flex justify-center -mt-16 md:justify-end">
              <LuBusFront
                className={`object-cover p-2 w-20 h-20 float-left rounded-full block border-2 bg-[#FCAE52] border-[#FCAE52] dark:border-[#FCAE52]`}
              />
            </div>

            <h2 className="mt-2 text-xl font-semibold text-gray-800 dark:text-white md:mt-0">
              Siniestros TUS - Circulación
            </h2>

            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md ring-1 ring-inset ring-gray-600/50">
              2025 – {currentYear}
            </span>

            <div className="flex flex-col">
              {/* Cantidad */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Se han registrado
                <span className="font-semibold underline decoration-[#FCAE52] px-1">
                  {fetchedIncidents?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedIncidents?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : (
                    fetchedIncidents?.data?.filter(
                      (incident) => incident?.type === 'circulation'
                    )?.length
                  )}
                </span>
                siniestros TUS de circulación.
              </div>

              {/* Última importación */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Última importación:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="mx-1 animate-spin" />
                  ) : circulationLastImportLog ? (
                    <>{circulationLastImportDate}.</>
                  ) : null}
                </span>
              </div>

              {/* Último importador */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Último importador:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : circulationLastImportLog ? (
                    <>{circulationFirstName + circulationLastName}.</>
                  ) : null}
                </span>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Link
                title="Ver: Siniestros TUS - Circulación"
                href={`/tus-incidents/circulation/grid`}
                className="flex items-center justify-center space-x-2 font-medium text-[#FCAE52] dark:text-[#FCAE52]"
                tabIndex="0"
                role="link"
              >
                <span>Ir</span>
                <LuMoveRight size={20} />
              </Link>
            </div>
          </div>

          {/* Siniestros TUS - Lesionados - (General) */}
          <div className="w-full max-w-md col-span-12 px-8 py-4 mt-16 bg-white rounded-lg shadow-lg md:col-span-6 xl:col-span-4 dark:bg-gray-800">
            <div className="flex justify-center -mt-16 md:justify-end">
              <LuUser
                className={`object-cover p-2 w-20 h-20 float-left rounded-full block border-2 bg-[#FCAE52] border-[#FCAE52] dark:border-[#FCAE52]`}
              />
            </div>

            <h2 className="mt-2 text-xl font-semibold text-gray-800 dark:text-white md:mt-0">
              Siniestros TUS - Lesionados
            </h2>

            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md ring-1 ring-inset ring-gray-600/50">
              2025 – {currentYear}
            </span>

            <div className="flex flex-col">
              {/* Cantidad */}
              <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                Se han registrado
                <span className="font-semibold underline decoration-[#FCAE52] px-1">
                  {fetchedIncidents?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedIncidents?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : (
                    fetchedIncidents?.data?.filter(
                      (incident) => incident?.type === 'injured'
                    )?.length
                  )}
                </span>
                siniestros TUS de lesionados.
              </div>

              {/* Última importación */}
              <div className="flex flex-wrap items-center mt-2 space-x-1 text-sm text-gray-600 dark:text-gray-200">
                Última importación:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : injuredLastImportLog ? (
                    <>{injuredLastImportDate}.</>
                  ) : null}
                </span>
              </div>

              {/* Último importador */}
              <div className="flex flex-wrap items-center mt-2 space-x-1 text-sm text-gray-600 dark:text-gray-200">
                Último importador:
                <span className="font-semibold underline decoration-[#FCAE52] pl-1">
                  {fetchedLogs?.error ? (
                    <LuX className="text-red-600" size={20} />
                  ) : fetchedLogs?.loading ? (
                    <ImSpinner9 size={16} className="animate-spin" />
                  ) : injuredLastImportLog ? (
                    <>{injuredFirstName + injuredLastName}.</>
                  ) : null}
                </span>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Link
                title="Ver: Siniestros TUS - Lesionados"
                href={`/tus-incidents/injured/grid`}
                className="flex items-center justify-center space-x-2 font-medium text-[#FCAE52] dark:text-[#FCAE52]"
                tabIndex="0"
                role="link"
              >
                <span>Ir</span>
                <LuMoveRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

DashboardComp.propTypes = {
  fetchedIncidents: PropTypes.object,
  fetchedLogs: PropTypes.object
}
